import { motion } from "framer-motion";
import { useLang } from "@/contexts/LanguageContext";
import { useT } from "@/lib/i18n";
import { HOME } from "@/constants/testIds";

const CLIP_SHAPES = ["arch-top", "blob-clip", "pill-clip", "arch-top", "blob-clip", "pill-clip"];
const TINTS = [
    { bg: "#FFE4EE", tag: "#E83A74" },
    { bg: "#DFF6F3", tag: "#0B7A75" },
    { bg: "#F0E5FF", tag: "#8B5FBF" },
    { bg: "#FFF3D6", tag: "#B08600" },
    { bg: "#FFE4EE", tag: "#E83A74" },
    { bg: "#DFF6F3", tag: "#0B7A75" },
];

const Catalogue = ({ products = [] }) => {
    const { lang } = useLang();
    const t = useT(lang);

    return (
        <section
            id="catalogue"
            data-testid={HOME.catalogueSection}
            className="relative py-28 md:py-40 overflow-hidden"
            style={{ background: "var(--bg-secondary)" }}
        >
            <div className="mx-auto max-w-[1600px] px-6 md:px-10">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 mb-16 md:mb-24">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="md:col-span-4"
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <span className="w-8 h-px" style={{ background: "var(--pink)" }} />
                            <span className="font-body text-[11px] uppercase tracking-[0.32em]" style={{ color: "var(--pink)" }}>
                                {t("catalogue.eyebrow")}
                            </span>
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight" style={{ color: "var(--ink-1)" }}>
                            {t("catalogue.title")}
                        </h2>
                    </motion.div>
                    <motion.p
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="md:col-span-6 md:col-start-7 self-end font-body text-lg md:text-xl leading-[1.55]"
                        style={{ color: "var(--ink-2)" }}
                    >
                        {t("catalogue.desc")}
                    </motion.p>
                </div>

                <div className="grid grid-cols-12 auto-rows-[220px] md:auto-rows-[260px] gap-4 md:gap-8">
                    {products.map((p, i) => {
                        const clip = CLIP_SHAPES[i % CLIP_SHAPES.length];
                        const tint = TINTS[i % TINTS.length];
                        return (
                            <motion.article
                                key={p.id}
                                data-testid={HOME.catalogueCard(p.slug)}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.7, delay: (i % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                                whileHover={{ y: -8 }}
                                className={`group relative ${p.span || "col-span-12 md:col-span-6"}`}
                            >
                                <div className="relative w-full h-full overflow-hidden" style={{ background: tint.bg }}>
                                    <div className={`absolute inset-3 md:inset-4 overflow-hidden ${clip}`}>
                                        <motion.img
                                            src={p.image}
                                            alt={lang === "es" ? p.title_es : p.title_en}
                                            loading="lazy"
                                            className="w-full h-full object-cover"
                                            whileHover={{ scale: 1.06 }}
                                            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                                        />
                                    </div>
                                    {/* spotlight */}
                                    <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: "radial-gradient(400px circle at 50% 30%, rgba(255,255,255,0.35), transparent 60%)" }} />

                                    {/* index label */}
                                    <div className="absolute top-4 left-4 flex items-center gap-2 font-body text-[10px] uppercase tracking-[0.24em]" style={{ color: tint.tag }}>
                                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: tint.tag }} />
                                        {String(i + 1).padStart(2, "0")} · {p.category}
                                    </div>

                                    {/* text */}
                                    <div className="absolute bottom-0 inset-x-0 p-5 md:p-6 flex items-end justify-between gap-4" style={{ background: "linear-gradient(180deg, transparent 0%, rgba(255,240,245,0.94) 60%)" }}>
                                        <div>
                                            <h3 className="font-serif text-2xl md:text-3xl leading-[1.05] tracking-tight" style={{ color: "var(--ink-1)" }}>
                                                {lang === "es" ? p.title_es : p.title_en}
                                            </h3>
                                            <p className="hidden md:block font-body text-sm mt-1 max-w-[38ch]" style={{ color: "var(--ink-2)" }}>
                                                {lang === "es" ? p.description_es : p.description_en}
                                            </p>
                                        </div>
                                        <a
                                            href="#contact"
                                            className="shrink-0 inline-flex items-center gap-2 font-body text-[11px] uppercase tracking-[0.24em] px-3 py-2 rounded-full whitespace-nowrap"
                                            style={{ background: "var(--ink-1)", color: "#fff" }}
                                        >
                                            {lang === "es" ? "Reservar" : "Reserve"} →
                                        </a>
                                    </div>
                                </div>
                            </motion.article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default Catalogue;
